// src/components/SoundToggle.js
import React, { useState, useEffect } from "react";
import audioService from "./AudioService";

const SoundToggle = ({
  padNumber = "1",
  tankNumber = "1",
  isInTrapezium = false,
  panelMode = "white",
  defaultActive = true,
  onToggle = () => {},
  sendPostRequest = false,
  isSticky = false,
  inDrawer = false
}) => {
  const [isPlaying, setIsPlaying] = useState(defaultActive); // Use defaultActive prop
  const [isAudioBlocked, setIsAudioBlocked] = useState(false);
  const [isInCombinedSection, setIsInCombinedSection] = useState(false);
  const [isNavMenuOpen, setIsNavMenuOpen] = useState(false);

  // Reset to playing and unmute when navigating to a new pad (pollutant)
  useEffect(() => {
    setIsPlaying(defaultActive);
    if (defaultActive) {
      audioService.setMuted(false);
    }
  }, [padNumber, defaultActive]);

  // Scroll & nav-menu UI detection
  useEffect(() => {
    const handleScroll = () => {
      const slider = document.querySelector('.slider-container');
      if (slider) setIsInCombinedSection(slider.getBoundingClientRect().bottom <= 0);
    };
    const checkNav = () => setIsNavMenuOpen(!!document.querySelector('.nav-menu.open'));

    handleScroll();
    checkNav();

    const observer = new MutationObserver(checkNav);
    const navMenu = document.querySelector('.nav-menu');
    if (navMenu) observer.observe(navMenu, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('scroll', handleScroll);
    return () => { window.removeEventListener('scroll', handleScroll); observer.disconnect(); };
  }, []);

  // Play sound on mount or pad change
  useEffect(() => {
    const attemptPlay = async () => {
      // Update blocked state
      const blocked = !audioService.audioContext || audioService.audioContext.state !== 'running';
      setIsAudioBlocked(blocked);

      const isRunning = !blocked;

      // Correct check: use activeSources for non-ambient pads (currentlyPlayingExclusiveLoop is never set)
      const isAlreadyPlaying = (String(padNumber) === String(audioService.ambientSoundPad))
        ? !!audioService.ambientAudioSource
        : !!audioService.activeSources[String(padNumber)];

      // If we should be MUTED but sound is playing, STOP IT
      if (!isPlaying || audioService.isMuted) {
        if (isAlreadyPlaying) {
          if (String(padNumber) === String(audioService.ambientSoundPad)) {
            await audioService.stopAmbientSound(500);
          } else {
            await audioService.stopPadSound(padNumber, 500);
          }
        }
        return false;
      }

      // If we should be PLAYING but sound is NOT playing, START IT
      if (!isAlreadyPlaying) {
        // If we are playing a specific pollutant (not ambient), stop ambient first
        if (String(padNumber) !== String(audioService.ambientSoundPad)) {
          await audioService.stopAmbientSound(500);
        }
        
        // Final safety: check if we still want to play after potential await
        if (!isPlaying || audioService.isMuted || audioService.isLoadingScreen) return false;

        audioService.playPadSound(padNumber, {
          loop: true,
          tankNumber: tankNumber,
          fadeIn: true,
          fadeInDuration: 1000
        });
        return true;
      }

      return isAlreadyPlaying;
    };

    // Initial attempt
    attemptPlay();

    // Sync interval to catch when audio unblocks from an external interaction
    const interval = setInterval(() => {
      attemptPlay();
    }, 1000);

    return () => {
      clearInterval(interval);
      if (isPlaying) {
        if (String(padNumber) === String(audioService.ambientSoundPad)) {
          audioService.stopAmbientSound(500).catch(() => {});
        } else {
          audioService.stopPadSound(padNumber, 500).catch(() => {});
        }
      }
    };
  }, [padNumber, isPlaying, tankNumber]);

  const handleToggle = async () => {
    // If UI shows "playing" but context is suspended (browser blocked autoplay),
    // this click should START audio — not toggle it OFF.
    const contextBlocked = !audioService.audioContext
      || audioService.audioContext.state === 'suspended'
      || audioService.audioContext.state === 'closed';

    if (isPlaying && contextBlocked) {
      // First meaningful gesture — resume context and start audio immediately.
      audioService.setMuted(false);
      if (audioService.audioContext) {
        audioService.audioContext.resume().then(async () => {
          setIsAudioBlocked(audioService.audioContext.state !== 'running');
          if (audioService.audioContext.state === 'running') {
            if (String(padNumber) !== String(audioService.ambientSoundPad)) {
              await audioService.stopAmbientSound(500);
            }
            await audioService.playPadSound(padNumber, {
              loop: true,
              tankNumber: tankNumber,
              fadeIn: true,
              fadeInDuration: 1000
            });
          }
        }).catch(() => { });
      }
      return;
    }

    // Normal toggle
    const newPlaying = !isPlaying;
    setIsPlaying(newPlaying);

    if (!newPlaying) {
      await audioService.setMuted(true);
      audioService.stopAllSounds(0).catch(() => {});
    } else {
      await audioService.setMuted(false);
    }

    onToggle(newPlaying);
  };

  const buttonColor = isNavMenuOpen
    ? "white"
    : (isInCombinedSection
      ? (panelMode === "white" ? "black" : "white")
      : (isInTrapezium ? "white" : (panelMode === "white" ? "black" : "white")));

  // When inside the trapezium section, always use white spans for visibility
  const spanColor = isInTrapezium ? 'white' : buttonColor;

  return (
    <button
      className="sound-button"
      onClick={(e) => {
        e.stopPropagation();
        handleToggle();
      }}
      style={{
        position: inDrawer
          ? "relative"
          : (isSticky ? "relative" : "fixed"),

        top: inDrawer ? "auto" : (isSticky ? "auto" : "20px"),
        left: "auto",
        right: inDrawer ? "auto" : (isSticky ? "auto" : "20px"),

        background: "transparent",
        border: "none",
        cursor: "pointer",
        padding: "10px",
        zIndex: 20000,
        mixBlendMode: isInTrapezium ? "difference" : "normal"
      }}
      title={isAudioBlocked && isPlaying ? "Click to enable sound" : ""}
    >
      <div className={`sound-icon ${isPlaying ? "playing" : "muted"} ${isAudioBlocked && isPlaying ? "blocked" : ""}`}>
        <span style={{ background: spanColor }}></span>
        <span style={{ background: spanColor }}></span>
        <span style={{ background: spanColor }}></span>
        <span style={{ background: spanColor }}></span>
      </div>
    </button>
  );
};

export default SoundToggle;
